<template>
  <!-- Span wrapper lets tippy attach to a hoverable element when the button
       is disabled — native disabled blocks pointer events at browser level. -->
  <span
    v-tooltip="isEffectivelyDisabled ? effectiveTooltip : null"
    :class="{
      'btn-wrapper--disabled': isEffectivelyDisabled,
      'btn-wrapper--fullwidth': isEffectivelyDisabled && isFullWidth,
    }"
  >
    <button
      ref="containerRef"
      v-tooltip="isEffectivelyDisabled ? null : effectiveTooltip"
      class="button"
      :class="buttonClasses"
      :type="type"
      :disabled="isEffectivelyDisabled"
      v-bind="$attrs"
      @click="$emit('click', $event)"
    >
      <span v-if="hasIcon || loading" class="icon">
        <i v-if="loading" class="fa-solid fa-spin fa-spinner" />
        <slot v-else name="icon">
          <i :class="normalizedIcon" />
        </slot>
      </span>
      <span v-if="showLabel" class="btn__label">
        <slot>{{ label }}</slot>
      </span>
      <span v-if="hasRightIcon" class="icon">
        <slot name="right-icon">
          <i :class="normalizedRightIcon" />
        </slot>
      </span>
    </button>
  </span>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue'

function normalizeIconProp(icon) {
  if (!icon) return null
  if (Array.isArray(icon)) return icon
  return icon.split(' ')
}

export default {
  name: 'BaseButton',
  inheritAttrs: false,
  props: {
    label: { type: String, default: '' },
    icon: { type: [String, Array], default: null },
    rightIcon: { type: [String, Array], default: null },
    variant: { type: String, default: null },
    size: { type: String, default: null },
    outlined: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    compact: {
      type: String,
      default: 'never',
      validator: (v) => ['auto', 'always', 'never'].includes(v),
    },
    compactThreshold: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    tooltip: { type: [String, Object], default: null },
    type: { type: String, default: 'button' },
  },
  emits: ['click'],
  setup(props, { slots }) {
    const containerRef = ref(null)
    const isNarrow = ref(false)
    const attrs = useAttrs()
    let ro

    onMounted(() => {
      if (props.compact === 'auto' && containerRef.value) {
        const target =
          containerRef.value.closest('main') ||
          document.querySelector('main') ||
          containerRef.value
        ro = new ResizeObserver(([entry]) => {
          isNarrow.value = entry.contentRect.width < props.compactThreshold
        })
        ro.observe(target)
      }
    })
    onBeforeUnmount(() => ro?.disconnect())

    const normalizedIcon = computed(() => normalizeIconProp(props.icon))
    const normalizedRightIcon = computed(() =>
      normalizeIconProp(props.rightIcon),
    )
    const hasIcon = computed(() => !!props.icon || !!slots.icon)
    const hasRightIcon = computed(
      () => !!props.rightIcon || !!slots['right-icon'],
    )

    const showLabel = computed(() => {
      if (props.compact === 'always') return false
      if (props.compact === 'auto' && isNarrow.value) return false
      return !!(props.label || slots.default)
    })

    const buttonClasses = computed(() => {
      const classes = []
      if (props.variant) classes.push(`is-${props.variant}`)
      if (props.size) classes.push(`is-${props.size}`)
      if (props.outlined) classes.push('is-outlined')
      if (props.rounded) classes.push('is-rounded')
      return classes
    })

    const effectiveTooltip = computed(() => {
      if (props.tooltip) return props.tooltip
      if (props.compact === 'always' && props.label) {
        return { content: props.label, html: true }
      }
      if (props.compact === 'auto' && isNarrow.value && props.label) {
        return { content: props.label, html: true }
      }
      return null
    })

    const isEffectivelyDisabled = computed(
      () => props.disabled || props.loading,
    )

    const isFullWidth = computed(() => {
      const cls = attrs.class || ''
      const s = Array.isArray(cls) ? cls.join(' ') : String(cls)
      return s.includes('is-fullwidth')
    })

    return {
      containerRef,
      isEffectivelyDisabled,
      isFullWidth,
      normalizedIcon,
      normalizedRightIcon,
      hasIcon,
      hasRightIcon,
      showLabel,
      buttonClasses,
      effectiveTooltip,
    }
  },
}
</script>

<style lang="scss" scoped>
// Span wrapper gives tippy a hoverable anchor when the button is disabled
// (native disabled blocks pointer events at browser level, not overridable via CSS).
.btn-wrapper--disabled {
  display: inline-flex;

  &.btn-wrapper--fullwidth {
    display: block;
    width: 100%;
  }
}

button > .btn__label:empty,
button > .icon:empty {
  display: none;
}

.btn__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Ghost variant: transparent, link-like text
button.is-ghost {
  background: none;
  border-color: transparent;
  color: var(--primary);
  box-shadow: none;
  text-decoration: none !important;

  .btn__label {
    border-bottom: 1px dotted currentColor;
    padding-bottom: 1px;
  }

  &:hover,
  &:focus-visible {
    background: var(--bg-muted);

    .btn__label {
      border-bottom-color: transparent;
    }
  }
}
</style>
